import React from 'react';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import buildInfo from '../buildInfo.json';

/**
 * Yeni bir sürüm (service worker) hazır olduğunda ekrana modern bir popup
 * gösterir. Kullanıcı "Şimdi Güncelle"ye basınca bekleyen SW aktifleştirilir
 * ve sayfa yenilenir. Oturum/çerezler korunur.
 */
function UpdateNotification() {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const registrationRef = React.useRef(null);

  React.useEffect(() => {
    const handleSwUpdate = (event) => {
      registrationRef.current =
        (event && event.detail) || window.__DIA_SW_REGISTRATION || null;
      setOpen(true);
    };
    window.addEventListener('diaSwUpdate', handleSwUpdate);

    // Bileşen geç mount olduysa bekleyen bir SW zaten olabilir
    if (window.__DIA_SW_REGISTRATION) {
      registrationRef.current = window.__DIA_SW_REGISTRATION;
      setOpen(true);
    } else if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistration()
        .then((reg) => {
          if (reg && reg.waiting) {
            registrationRef.current = reg;
            setOpen(true);
          }
        })
        .catch(() => {});
    }

    return () => window.removeEventListener('diaSwUpdate', handleSwUpdate);
  }, []);

  const handleUpdate = () => {
    setBusy(true);
    const registration = registrationRef.current;
    const waiting = registration && registration.waiting;
    if (waiting) {
      // Yeni SW devreye girsin; serviceWorker.js controllerchange'de sayfayı
      // yeniler. Yine de güvenlik için bir fallback reload bırakıyoruz.
      waiting.postMessage({ type: 'SKIP_WAITING' });
      setTimeout(() => window.location.reload(), 2500);
    } else {
      window.location.reload();
    }
  };

  const version =
    (buildInfo && buildInfo.version) || process.env.REACT_APP_VERSION;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!busy) setOpen(false);
      }}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          p: { xs: 2.5, sm: 3.5 },
          maxWidth: 380,
          width: '100%',
          textAlign: 'center',
          overflow: 'visible'
        }
      }}
    >
      <Box
        sx={{
          width: 92,
          height: 92,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FFB74D 0%, #FB8C00 100%)',
          boxShadow: '0 10px 24px rgba(251, 140, 0, 0.45)',
          animation: 'diaUpdatePulse 2.2s ease-in-out infinite',
          '@keyframes diaUpdatePulse': {
            '0%': {
              boxShadow:
                '0 10px 24px rgba(251,140,0,0.45), 0 0 0 0 rgba(251,140,0,0.45)'
            },
            '70%': {
              boxShadow:
                '0 10px 24px rgba(251,140,0,0.25), 0 0 0 18px rgba(251,140,0,0)'
            },
            '100%': {
              boxShadow:
                '0 10px 24px rgba(251,140,0,0.45), 0 0 0 0 rgba(251,140,0,0)'
            }
          }
        }}
      >
        <RocketLaunchRoundedIcon sx={{ fontSize: 46, color: '#fff' }} />
      </Box>

      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#263238', mb: 1 }}>
        Yeni Sürüm Hazır 🚀
      </Typography>

      <Typography
        sx={{ fontSize: 14, color: '#607D8B', lineHeight: 1.6, mb: 1.5 }}
      >
        Uygulamanın güncel sürümü yayınlandı. En son özellikler ve
        iyileştirmeler için lütfen güncelleyin. Oturumunuz ve verileriniz
        korunur.
      </Typography>

      {version && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box
            sx={{
              px: 1.2,
              py: 0.3,
              borderRadius: '999px',
              backgroundColor: 'rgba(255,152,0,0.12)',
              color: '#FB8C00',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.3
            }}
          >
            Sürüm {version}
          </Box>
        </Box>
      )}

      <Button
        onClick={handleUpdate}
        disabled={busy}
        fullWidth
        variant="contained"
        startIcon={
          busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : null
        }
        sx={{
          borderRadius: '14px',
          py: 1.2,
          fontWeight: 700,
          fontSize: 15,
          textTransform: 'none',
          background: 'linear-gradient(135deg, #FFA726 0%, #FB8C00 100%)',
          boxShadow: '0 8px 18px rgba(251,140,0,0.4)',
          '&:hover': {
            background: 'linear-gradient(135deg, #FB8C00 0%, #F57C00 100%)'
          }
        }}
      >
        {busy ? 'Güncelleniyor…' : 'Şimdi Güncelle'}
      </Button>
    </Dialog>
  );
}

export default UpdateNotification;
